import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Table, Input, Button, Space, Tag, Switch, Tooltip, Card, Typography, Row, Col, Spin, message, Modal } from 'antd';
import { SearchOutlined, EditOutlined, SyncOutlined, EyeOutlined } from '@ant-design/icons';
import { useProductProperties, useUpdateProductStatus, useUpdateProductOnlineStatus } from '../hooks/useProductProperties';
import { useMigrateAccountMappings } from '@/shared/api/client/account-mapping-resource';
import {ProductPropertiesDTO} from "@/shared/api/model";
import LoanProductPreviewPanel from '../components/LoanProductPreviewPanel';

const { Title } = Typography;

const LoanProductSettingsListView: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewProduct, setPreviewProduct] = useState<ProductPropertiesDTO | null>(null);

  const { data: productsData, isLoading, isError, refetch } = useProductProperties({
    page: currentPage,
    size: pageSize,
    sort: 'productName,asc'
  });

  // Mutation to update product status
  const updateProductStatus = useUpdateProductStatus();

  // Mutation to update online status
  const updateProductOnline = useUpdateProductOnlineStatus();

  // Mutation for account mappings migration
  const migrateAccountMappings = useMigrateAccountMappings({
    mutation: {
      onSuccess: (response) => {
        message.success('Account mappings migrated successfully!');
        console.log('Migration result:', response.data);
        // Optionally refetch products data to reflect any changes
        refetch();
      },
      onError: (error) => {
        console.error('Migration failed:', error);
        message.error('Failed to migrate account mappings. Please try again.');
      }
    }
  });

  // Handle migration
  const handleMigration = () => {
    migrateAccountMappings.mutate();
  };

  // Handle status toggle
  const handleStatusToggle = (glNo: string, isActive: boolean) => {
    updateProductStatus.mutate({ 
      glNo, 
      params: { isActive: !isActive } 
    });
  };

  // Handle online toggle
  const handleOnlineToggle = (glNo: string, online: boolean) => {
    updateProductOnline.mutate({
      glNo,
      params: { online: !online }
    });
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(0); // Reset to first page when searching
  };

  // Filter data based on search
  const filteredData = React.useMemo(() => {
    if (!productsData?.content) return [];

    return productsData?.content.filter(item =>
      item.productName?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.productCode?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [productsData, searchText]);

  // Table columns
  const columns = [
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      sorter: (a: ProductPropertiesDTO, b: ProductPropertiesDTO) =>
        (a.productName || '').localeCompare(b.productName || ''),
    },
    {
      title: 'GL No',
      dataIndex: 'glNo',
      key: 'glNo',
    },
    {
      title: 'Interest Rate',
      dataIndex: 'baseInterestRate',
      key: 'baseInterestRate',
      render: (rate: number) => (rate ? `${rate}%` : 'N/A'),
    },
    {
      title: 'Repayment Type',
      dataIndex: 'repaymentScheduleType',
      key: 'repaymentScheduleType',
      render: (type: string) => {
        if (!type) return 'N/A';
        
        // Convert snake case to title case for display
        return type.replace(/_/g, ' ').replace(/\w\S*/g, 
          txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: ProductPropertiesDTO) => (
        <Switch 
          checked={isActive} 
          onChange={() => handleStatusToggle(record.glNo, isActive || false)}
          loading={updateProductStatus.isPending && record.glNo === updateProductStatus.variables?.glNo}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Online',
      dataIndex: 'online',
      key: 'online',
      render: (online: boolean, record: ProductPropertiesDTO) => (
        <Switch 
          checked={online} 
          onChange={() => handleOnlineToggle(record.glNo, online || false)}
          loading={updateProductOnline.isPending && record.glNo === updateProductOnline.variables?.glNo}
          checkedChildren="Online"
          unCheckedChildren="Offline"
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: ProductPropertiesDTO) => (
        <Space size="middle">
          <Tooltip title="Edit Settings">
            <Link to={`/admin/loan-settings/products/${record.glNo}`}>
              <Button 
                type="primary" 
                icon={<EditOutlined />} 
                size="small"
              >
                Configure
              </Button>
            </Link>
          </Tooltip>
          <Tooltip title="Preview Loan Forms">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => {
                setPreviewProduct(record);
                setIsPreviewOpen(true);
              }}
            >
              Preview Form
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <Typography.Text type="danger">Failed to load loan product settings. Please try again.</Typography.Text>
      </div>
    );
  }

  console.log('ProductProperties data:', productsData); // For debugging
  console.log('Filltred data:', filteredData); // For debugging

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4}>Loan Product Settings</Title>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Migrate account mappings from COA to new structure">
              <Button 
                type="default"
                icon={<SyncOutlined />}
                onClick={handleMigration}
                loading={migrateAccountMappings.isPending}
              >
                Migrate Account Mappings
              </Button>
            </Tooltip>
            <Input 
              placeholder="Search by name or code" 
              prefix={<SearchOutlined />} 
              onChange={e => handleSearch(e.target.value)}
              value={searchText}
              allowClear
            />
          </Space>
        </Col>
      </Row>

      <Table 
        columns={columns} 
        dataSource={filteredData}
        rowKey="glNo"
        pagination={{
          current: currentPage + 1,
          pageSize: pageSize,
          total: productsData?.totalElements || 0,
          onChange: (page, pageSize) => {
            setCurrentPage(page - 1);
            setPageSize(pageSize);
          },
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />

      <Modal
        title={`Loan Form Preview${previewProduct?.productName ? ` - ${previewProduct.productName}` : ''}`}
        open={isPreviewOpen}
        onCancel={() => {
          setIsPreviewOpen(false);
          setPreviewProduct(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsPreviewOpen(false);
              setPreviewProduct(null);
            }}
          >
            Close
          </Button>
        ]}
        width={900}
        bodyStyle={{ padding: 20 }}
      >
        {previewProduct ? (
          <LoanProductPreviewPanel
            glNo={previewProduct.glNo}
            productName={previewProduct.productName || previewProduct.glNo}
            baseInterestRate={Number(previewProduct.baseInterestRate)}
            defaultAmount={Number(previewProduct.defaultAmount) || 1000000}
            defaultTerm={previewProduct.defaultTerm ?? 12}
            requiresDeductionForm={previewProduct.requiresDeductionForm ?? false}
          />
        ) : null}
      </Modal>
    </Card>
  );
};

export default LoanProductSettingsListView;
